import { Trans, useLingui } from '@lingui/react/macro';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { redirectToExternalUrl } from 'modules/organizer/domain/utils/redirect-to-external-url';
import { WalletTopup } from 'modules/organizer/domain/interfaces/wallet-topup';
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { checkIsDev } from 'shared/utils/check-is-dev';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';
import { SubscriptionSbpPayTestForm } from './subscription-sbp-pay-test-form';
import UDCard from 'ud-ui/components/card';

interface SubscriptionQrStepProps {
  topup: WalletTopup;
  onRetry: () => void;
}

export const SubscriptionQrStep: React.FC<SubscriptionQrStepProps> = ({ topup, onRetry }) => {
  const { t } = useLingui();
  const amountLabel = `${formatRublesFromMinorUnits(topup.amountInMinorUnits)} ₽`;
  const isCard = topup.paymentMethod === 'CARD';

  if (topup.status === 'REFUNDED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">
          <Trans>Платёж возвращён</Trans>
        </UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          <Trans>Банк вернул оплату. Создайте новый платёж, если хотите пополнить баланс снова.</Trans>
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          <Trans>Попробовать снова</Trans>
        </UDButton>
      </div>
    );
  }

  if (topup.status === 'REJECTED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">
          <Trans>Платёж отклонён</Trans>
        </UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          {topup.errorMessage ??
            (isCard
              ? t`Банк отклонил оплату картой. Создайте новый платёж и попробуйте ещё раз.`
              : t`Банк отклонил оплату. Создайте новый QR-код и попробуйте ещё раз.`)}
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          <Trans>Попробовать снова</Trans>
        </UDButton>
      </div>
    );
  }

  if (topup.status === 'EXPIRED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">{isCard ? t`Платёж истёк` : t`QR-код истёк`}</UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          {isCard ? t`Создайте новый платёж, чтобы оплатить картой.` : t`Создайте новый платёж, чтобы получить актуальный код.`}
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          {isCard ? t`Создать новый платёж` : t`Создать новый QR`}
        </UDButton>
      </div>
    );
  }

  if (isCard) {
    return (
      <div className="flex flex-col items-start gap-4">
        <UDText type="headline">
          <Trans>Оплата картой</Trans>
        </UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          <Trans>Оплатите картой на странице банка. Сумма: {amountLabel}</Trans>
        </UDText>
        {topup.paymentUrl && (
          <UDButton type="button" variant="secondary" onClick={() => redirectToExternalUrl(topup.paymentUrl as string)}>
            <Trans>Открыть страницу банка снова</Trans>
          </UDButton>
        )}
        <UDText type="footnote" color={theme.colors.Gray}>
          <Trans>Ожидаем подтверждение оплаты…</Trans>
        </UDText>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-4">
      <UDText type="headline">
        <Trans>Оплата по СБП</Trans>
      </UDText>
      <UDText type="body-text" color={theme.colors.Gray}>
        <Trans>Отсканируйте QR-код в приложении банка. Сумма: {amountLabel}</Trans>
      </UDText>
      {topup.qrPayload && (
        <UDCard padding="sm">
          <QRCodeSVG value={topup.qrPayload} size={220} />
        </UDCard>
      )}
      {topup.qrPayload && (
        <UDButton type="button" variant="secondary" onClick={() => window.open(topup.qrPayload as string, '_blank')}>
          <Trans>Открыть в банке</Trans>
        </UDButton>
      )}
      <UDText type="footnote" color={theme.colors.Gray}>
        <Trans>Ожидаем подтверждение оплаты…</Trans>
      </UDText>
      {checkIsDev() && <SubscriptionSbpPayTestForm topupId={topup.id} />}
    </div>
  );
};
